import { Permission, PLATFORM_PERMISSIONS } from '../../auth/permissions/permission.js';

/** The immutable system role that denotes a full administrator. */
export const ADMINISTRATOR_ROLE_NAME = 'Administrator';

/** The immutable system role assigned to users joining seat-based organisations. */
export const SEAT_BASED_ROLE_NAME = 'Seat-based';

/** The seeded role assigned as every new organisation's default. */
export const MEMBER_ROLE_NAME = 'Member';

/** The immutable system role with no permissions. */
export const READ_ONLY_ROLE_NAME = 'Read-only';

export const SYSTEM_ROLE_NAMES = [
  ADMINISTRATOR_ROLE_NAME,
  READ_ONLY_ROLE_NAME,
  SEAT_BASED_ROLE_NAME,
] as const;

// Platform permissions are always granted explicitly to roles in eligible organisations.
const ALL_ORGANISATION_PERMISSIONS = (Object.values(Permission) as Permission[]).filter(
  (permission) => !PLATFORM_PERMISSIONS.has(permission),
);

export const DEFAULT_USER_ROLES = {
  administrator: {
    name: ADMINISTRATOR_ROLE_NAME,
    permissions: ALL_ORGANISATION_PERMISSIONS,
  },

  analytics: {
    name: 'Analytics',
    permissions: [
      Permission.MatterCreate,
      Permission.MatterAddUsers,
      Permission.MatterViewUsers,
      // Document
      Permission.DocumentUpload,
      Permission.DocumentUpdate,
      Permission.DocumentDelete,
      Permission.DocumentDownload,
      // Subset
      Permission.SubsetCreate,
      Permission.SubsetEdit,
      // Fact
      Permission.FactCreate,
      Permission.FactUpdate,
      Permission.FactDownload,
      // Inconsistency
      Permission.InconsistencyEdit,
      // Chronology
      Permission.ChronologyCreate,
      Permission.ChronologyEdit,
      Permission.ChronologyDownload,
      // Doc Chronology
      Permission.DocChronologyCreate,
      Permission.DocChronologyEdit,
      Permission.DocChronologyDownload,
      // Comment
      Permission.CommentAdd,
      // Entities
      Permission.PersonDownload,
      // Thread
      Permission.ThreadCreate,
      Permission.ThreadPublish,
      Permission.ThreadDownload,
      Permission.ProceduralTimelineEdit,
      // Rel
      Permission.RelativityImport,
      // Org-level
      Permission.RealtimeUse,
      Permission.UsageDashboardView,
      Permission.AuditView,
    ],
  },

  readOnly: {
    name: READ_ONLY_ROLE_NAME,
    permissions: [],
  },

  member: {
    name: MEMBER_ROLE_NAME,
    permissions: [
      Permission.MatterCreate,
      Permission.MatterAddUsers,
      Permission.MatterViewUsers,
      // Document
      Permission.DocumentUpload,
      Permission.DocumentUpdate,
      Permission.DocumentDelete,
      Permission.DocumentDownload,
      // Subset
      Permission.SubsetCreate,
      Permission.SubsetEdit,
      // Fact
      Permission.FactCreate,
      Permission.FactUpdate,
      Permission.FactDownload,
      // Inconsistency
      Permission.InconsistencyEdit,
      // Chronology
      Permission.ChronologyCreate,
      Permission.ChronologyEdit,
      Permission.ChronologyDownload,
      // Doc Chronology
      Permission.DocChronologyCreate,
      Permission.DocChronologyEdit,
      Permission.DocChronologyDownload,
      // Comment
      Permission.CommentAdd,
      // Entities
      Permission.PersonDownload,
      // Thread
      Permission.ThreadCreate,
      Permission.ThreadPublish,
      Permission.ThreadDownload,
      Permission.ProceduralTimelineEdit,
      // Rel
      Permission.RelativityImport,
      // Org-level
      Permission.RealtimeUse,
    ],
  },

  seatBased: {
    name: SEAT_BASED_ROLE_NAME,
    permissions: [
      Permission.MatterCreate,
      Permission.MatterAddUsers,
      Permission.MatterViewUsers,
      // Document
      Permission.DocumentUpload,
      Permission.DocumentUpdate,
      Permission.DocumentDelete,
      Permission.DocumentDownload,
      // Subset
      Permission.SubsetCreate,
      Permission.SubsetEdit,
      Permission.SubsetDelete,
      // Fact
      Permission.FactCreate,
      Permission.FactUpdate,
      Permission.FactDelete,
      Permission.FactDownload,
      // Inconsistency
      Permission.InconsistencyEdit,
      // Chronology
      Permission.ChronologyCreate,
      Permission.ChronologyEdit,
      Permission.ChronologyDelete,
      Permission.ChronologyDownload,
      // Doc Chronology
      Permission.DocChronologyCreate,
      Permission.DocChronologyEdit,
      Permission.DocChronologyDelete,
      Permission.DocChronologyDownload,
      // Comment
      Permission.CommentAdd,
      Permission.CommentDelete,
      // Entities
      Permission.PersonDownload,
      Permission.PersonUpdate,
      Permission.PersonDelete,
      // Inconsistency
      Permission.InconsistencyDelete,
      Permission.InconsistencyDownload,
      Permission.InconsistencyEdit,
      // Thread
      Permission.ThreadCreate,
      Permission.ThreadPublish,
      Permission.ThreadDownload,
      Permission.ProceduralTimelineEdit,
      // Rel
      Permission.RelativityImport,
      // Org-level
      Permission.RealtimeUse,
      Permission.UsageDashboardView,
      Permission.IntegrationsManage,
      Permission.AuditView,
    ],
  },
};
